/* app/seo-bulkmeta.jsx - Bulk Meta Editor. Two reads, one screen.

   AT ONE SECTION it is what it has always been: GET /api/wp/bulk-meta (list) +
   PATCH /api/wp/seo-meta/:id (sparse save) through that section's own
   connection, with one title suffix budget read from the stored audit snapshot.
   Nothing on that path changed.

   AT THE WHOLE WEBSITE (the switcher's All position) it reads
   GET /api/wp/bulk-meta-property, which answers for every piece of the property
   at once, and the three things that were ONE thing per screen become per ROW:

     THE SECTION, named on every row, because a post id means nothing outside
     the CMS it came from and a union list that does not say where a row came
     from is a list nobody can act on.

     THE VERB. A row whose section has a CMS offers Save, AI Fill SEO and Apply
     template exactly as before. A row whose section has none offers Copy the
     handover brief, and the drawer says why in the endpoint's own words. This
     is what makes the kept nav entry true: docs/MODULE-SCOPE.md 4.2 records
     that the sidebar deliberately keeps Bulk Meta for a site with no CMS, and
     that the screen then said it was not available, so the one screen meant to
     survive having no CMS was an entry leading to a refusal.

     THE BUDGET. The title counter subtracts the suffix the last live crawl
     found, and a suffix is a property of a FRONT END, so a website with two
     front ends has two budgets. Each row carries its own section's suffix and
     is counted against it. There is deliberately no property-wide number
     anywhere on this screen at All, and the endpoint's `budget_why` says so on
     screen rather than leaving a reader to notice.

   AND NO SAVE ALL at All. Saving is per section, through that section's own
   address, because one button writing a hundred rows into three different CMSs
   is the one defect on this screen that cannot be undone from here. */
import { api, apiList } from '../lib/api.js';
import { capSecondary, secondaryAdvice, normaliseKeywords, MAX_SECONDARY, RULE_SENTENCE } from '../lib/keyword-rule.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const FD = (p) => React.createElement(window.MlBiHelpers.FacetDropdown, p);

  /* What a WordPress status, or a crawled page's reading of one, is called here.
     The crawl answers 'noindex' and 'broken' as well, which the old map turned
     into "Draft": a hand coded page that the front end tells Google to ignore
     is not a draft, and a page answering 500 is not a draft either. Both are
     facts about what the site SERVES and they get their own badge. */
  const STATUS_OF = { publish: "published", pending: "review", noindex: "noindex", broken: "broken" };

  /* One mapper for both reads, and the one rename this screen needed.

     `id` IS THE ROW'S IDENTITY AND IS NOT ALWAYS A POST ID. At a section it is
     the post id, exactly as before. At All it is `row_key`, because a crawled
     page has no post id at all (the endpoint sends null) and two sections can
     hold the same post id, so keying the table, the selection and the dirty
     set on a post id would make two real rows of two different websites fight
     over one piece of state. The post id travels beside it as `postId` and is
     used for exactly one thing: the PATCH that writes it, through its own
     section's address. */
  const mapBM = (it) => ({
    id: it.row_key != null ? it.row_key : it.id,
    postId: it.id == null ? null : it.id,
    title: it.post_title || "(no title)",
    status: STATUS_OF[it.status] || "draft",
    t: it.seo_title || "", d: it.meta_description || "", kw: it.focus_keyword || "", link: it.link || "",
    // Section attribution and capability, both rendered verbatim and neither
    // re-derived. Absent on the per section read, where the screen's one
    // connection answers for every row.
    sectionId: it.section_id == null ? null : it.section_id,
    sectionName: it.section_name || "",
    sectionSource: it.section_source || "",
    canSave: it.can_save === undefined ? true : !!it.can_save,
    verb: it.verb || "save",
    verbWhy: it.verb_why || null,
    suffix: typeof it.title_suffix === "string" ? it.title_suffix : "",
    readFrom: it.read_from || "cms",
  });

  /* ── How long a title may actually be ─────────────────────
     60 and 160 are what a search result shows, and for a long time the title
     counter said "36 / 60" and stopped there. That number is a lie on any site
     whose front end appends a suffix to every served title: the stored value is
     36 characters, the page serves 36 plus " | Capitaine Agile", and Google
     truncates what it SERVES, not what we store.

     It cost a real customer a round trip today. Titles were written at 58
     against this counter, served at 77, and flagged over the limit again by the
     very next crawl, which reads the rendered HTML and is therefore the only
     one of the two numbers that was ever right.

     So the budget is the limit MINUS whatever the site appends, and the screen
     says both halves out loud. Silently turning 60 into 42 would be the same
     failure pointed the other way: a counter nobody can explain is a counter
     people learn to override.

     The suffix comes from the stored audit snapshot (meta.title_suffix),
     because the public crawl is the only pass that sees a rendered <title>. It
     is ABSENT on a site that has never been crawled and NULL on a site with no
     such convention, and both of those mean the plain 60. This screen has to
     work on a site with no audit at all, so the read is best effort and never
     gates anything.

     AT ALL THERE IS NO SUCH THING AS "THE" SUFFIX. A suffix belongs to a front
     end, so the property read carries one per ROW, from that section's own
     stored snapshot, and `titleBudget` is called once per row. The audit read
     below does not even run there: a single number would be wrong for at least
     one section of any website that has two. */
  const TITLE_LIMIT = 60;
  const DESC_LIMIT  = 160;
  // What the writer is ASKED for, which sits under what is displayed. 160 is
  // where Google cuts; this is the target that leaves a few characters of
  // headroom so a generated description does not land exactly on the edge.
  const DESC_TARGET = 155;
  // A floor under the budget. A theme appending a 50 character company name
  // would otherwise leave 10, flag every title on the site, and be both
  // unarguable and useless: past this point the suffix is the thing to fix, not
  // the titles, and the note says so rather than the counter implying it.
  const MIN_TITLE_BUDGET = 25;

  function titleBudget(suffix) {
    // NOT trimmed. The crawl stores the suffix exactly as the page serves it,
    // which for the site that prompted all of this is " | Capitaine Agile":
    // eighteen characters, with a leading space the theme really does emit.
    // Trimming it here would quietly hand back a budget of 43 instead of 42
    // and reintroduce a one character version of the bug this is fixing. The
    // emptiness test trims, because a whitespace-only suffix is no convention.
    const s = typeof suffix === "string" ? suffix : "";
    if (!s.trim()) return { limit: TITLE_LIMIT, suffix: "", tight: false };
    const room = TITLE_LIMIT - s.length;
    return { limit: Math.max(room, MIN_TITLE_BUDGET), suffix: s, tight: room < MIN_TITLE_BUDGET };
  }

  // The sentence that explains the changed number, wherever the number shows.
  // "this site" rather than "this website" on purpose: the thing that appends a
  // suffix is one front end, and at All several of them are on screen at once.
  function suffixNote(b) {
    if (!b || !b.suffix) return null;
    return `${b.limit} is ${TITLE_LIMIT} less the ${b.suffix.length} characters this site appends to every title ("${b.suffix}"). `
      + "Google truncates what the page serves, not what is stored here."
      + (b.tight ? ` The suffix alone leaves less than ${MIN_TITLE_BUDGET} characters, so the budget is held at ${MIN_TITLE_BUDGET}: shortening the suffix is the fix, not shortening every title.` : "");
  }

  // `budget` is optional. Without it this is the plain counter it has always
  // been, which is what the description uses and what every title on a site
  // with no suffix uses.
  function counter(len, max, budget) {
    const sfx = budget && budget.suffix ? budget.suffix : "";
    const over = len > max;
    return (
      <span className={"seo-counter" + (over ? " over" : "")} title={sfx ? suffixNote(budget) : undefined}>
        {len} / {max}{over ? " · " + (len - max) + " over" : ""}
        {sfx ? <span className="seo-counter__suffix"> · site adds "{sfx}"</span> : null}
      </span>
    );
  }
  function statusBadge(s) {
    const { Badge } = window.HuddleDesignSystem_775113;
    const m = {
      published: ["success", "Published"], draft: ["warning", "Draft"], review: ["info", "In review"],
      // Read from the served page rather than from a CMS, which is why these
      // two exist at all. A fallback rather than a lookup that can throw: an
      // unknown status must not be able to blank a row somebody came to edit.
      noindex: ["warning", "Noindex"], broken: ["danger", "Not reachable"],
    }[s] || ["neutral", s || "unknown"];
    return <Badge tone={m[0]} dot>{m[1]}</Badge>;
  }

  /* ── The handover brief, composed HERE, which is a stated exception ──
     This screen's rule at All is that it renders what the endpoint decided and
     re-derives nothing. The brief is the one exception and it is deliberate:
     there is NO endpoint that turns a crawled page into a brief. Page gaps has
     one for a page that does not exist yet; this is the opposite case, a page
     that exists and whose title and description have to be changed by whoever
     owns the front end.

     So the text is composed in the client, out of what the row already carries
     and nothing else: the served URL, the title and description the crawl
     really read, the section it belongs to, the budget that section's own
     suffix leaves, and the endpoint's own sentence for why there is nothing to
     press here. No fact is invented and none is second guessed. If a route for
     this ever exists, this function is what it replaces. */
  function handoverBrief(row, bud) {
    const lines = [
      `Page to change: ${row.title}`,
      `URL: ${row.link || "(no address recorded)"}`,
      `Section: ${row.sectionName || "unknown"}${row.sectionSource === "crawl" ? " (no CMS: this section is read by crawling it)" : ""}`,
      "",
      `Title served now (${row.t.length} characters): ${row.t || "(none)"}`,
      `Description served now (${row.d.length} characters): ${row.d || "(none)"}`,
      "",
      `What has to fit: a title of ${bud.limit} characters or fewer`
        + (bud.suffix
          ? `, which is ${TITLE_LIMIT} less the "${bud.suffix}" this front end appends to every title it serves, because Google truncates what the page serves rather than what is stored.`
          : ".")
        + ` A description of ${DESC_LIMIT} characters or fewer.`,
      "",
      `Why this is a brief and not an edit: ${row.verbWhy || "this section has no CMS behind it, so there is nothing here to write to."}`,
      "",
      "These values were read by crawling the page, so they are what a visitor really gets rather than what a CMS claims.",
    ];
    return lines.join("\n");
  }

  /* ── The title template ───────────────────────────────────
     Two tokens and no more. `{title}` is the post's own headline and `{site}`
     is the site name, so `{title} | {site}` is the pattern almost everybody
     wants and the reason the button existed in the first place.

     Deliberately not an expression language. The moment a template can hold a
     conditional or a truncation it needs a parser, an error report and a
     screen of its own, and what it buys over two tokens is a pattern nobody
     can read back six months later. Anything beyond these two is typed into
     the row, where it is visible.

     The tidy at the end is not cosmetic. With the site name unknown, which is
     exactly what happens while the shell is still resolving the active site,
     `{title} | {site}` renders as "How to hire | " and that trailing separator
     would be saved to the live site on 90 rows. An empty token has to take its
     separator with it. */
  // What a bulk result's tone looks like. "error" is its own row rather than
  // folded into "warning": a batched fill that threw changed nothing at all,
  // and dressing that as a warning reads like a partial success. The two copy
  // tones are separate for the same reason: "AI Fill failed" over a clipboard
  // refusal would send somebody looking at the wrong button.
  const BULK_BANNER = {
    success: ["success", "Done, and nothing is saved yet"],
    warning: ["warning", "Check these before saving"],
    error  : ["danger",  "AI Fill failed"],
    info   : ["info",    "Nothing to do"],
    copied : ["success", "On your clipboard"],
    copy_failed: ["danger", "Could not copy"],
    saved  : ["success", "Saved"],
    save_failed: ["danger", "Some rows were not saved"],
  };

  const TPL_TOKENS = ["title", "site"];
  function renderTemplate(pattern, postTitle, siteName) {
    return String(pattern || "")
      .replace(/\{title\}/g, String(postTitle || ""))
      .replace(/\{site\}/g, String(siteName || ""))
      .replace(/\s+/g, " ")
      // Separators left dangling at either end by a token that resolved to
      // nothing. Only at the ENDS: a pipe in the middle is between two real
      // pieces of text and is the whole point of the pattern.
      .replace(/^[\s|:,·\-]+/, "")
      .replace(/[\s|:,·\-]+$/, "")
      .trim();
  }
  // A token the pattern names that this screen cannot fill. Reported rather
  // than left to render literally: "{keywrod} | Acme" saved across 90 posts is
  // a typo that costs a re-crawl to notice.
  function unknownTokens(pattern) {
    const found = String(pattern || "").match(/\{[^}]*\}/g) || [];
    const seen = [];
    for (const t of found) {
      const name = t.slice(1, -1).trim().toLowerCase();
      if (!TPL_TOKENS.includes(name) && !seen.includes(t)) seen.push(t);
    }
    return seen;
  }

  // ---- the keyword rule, in a table cell ----
  //
  // /wp/bulk-meta returns one focus_keyword string per post and the PATCH this
  // screen saves through writes that one column, so a row has a single keywords
  // cell rather than the editor's focus field plus its chip set. The cell is
  // read the way the editor's generate modal already reads its own input:
  // comma separated, strongest first. The first term is what the post targets,
  // and the rest are the secondaries it also wants.
  //
  // Nothing here decides the number. capSecondary and secondaryAdvice come from
  // the shared rule, so this screen cannot promise a fourth secondary that the
  // generator and the SEO panel will not honour.
  function readKeywords(text) {
    const all = normaliseKeywords(String(text === null || text === undefined ? "" : text).split(","));
    const primary = all[0] || "";
    const { secondary, spare } = capSecondary(all.slice(1), primary);
    // The advice is on what the post ASKS for, not on what fits. Counting
    // secondary.length alone would top out at three and never report the state
    // the save refuses.
    return { all, primary, secondary, spare, advice: secondaryAdvice(secondary.length + spare.length) };
  }

  // The one-line version, for a row. No primary is its own state and reads as
  // one: a post targeting nothing is not a post inside the rule.
  function kwLine(k) {
    if (!k.primary) return <span className="bm-kw bm-kw--none" title="Nothing is targeted yet, which is not the same as being within the rule.">No primary keyword</span>;
    const cls = k.advice.state === "over" ? " bm-kw--over" : k.advice.state === "on_target" ? " bm-kw--ok" : "";
    return (
      <span className={"bm-kw" + cls} title={k.advice.note}>
        <b>{k.primary}</b> + {k.advice.count} / {MAX_SECONDARY} secondary
        {k.spare.length > 0 ? <span className="bm-kw__over"> · over: {k.spare.join(", ")}</span> : null}
      </span>
    );
  }

  // The full version, for the drawer. `spare` is rendered rather than counted:
  // a post whose ninth keyword vanished on save with nothing said is the exact
  // failure the rule exists to make visible.
  function kwPanel(k) {
    if (!k.primary) {
      return <p className="bm-kw-panel bm-kw-panel--none">No primary keyword. That is not the same as being within the rule: this post targets nothing at all. The first term you type is the primary, and it owns the title, URL and opening.</p>;
    }
    return (
      <div className="bm-kw-panel">
        <div className="bm-kw-row">
          <span className="bm-kw-lab">Primary</span>
          <span className="bm-kw-chip bm-kw-chip--primary">{k.primary}</span>
        </div>
        <div className="bm-kw-row">
          <span className="bm-kw-lab">Secondary</span>
          {k.secondary.length ? k.secondary.map((s, i) => <span className="bm-kw-chip" key={i}>{s}</span>) : <span className="bm-kw--none">None yet</span>}
          <span className={"seo-counter" + (k.advice.state === "over" ? " over" : "")}>{k.advice.count} / {MAX_SECONDARY}</span>
        </div>
        {k.spare.length > 0 && (
          <div className="bm-kw-row">
            <span className="bm-kw-lab">Over the line</span>
            {k.spare.map((s, i) => <span className="bm-kw-chip bm-kw-chip--over" key={i}>{s}</span>)}
          </div>
        )}
        <p className={"bm-kw-note" + (k.advice.state === "over" ? " bm-kw-note--bad" : "")}>
          {k.advice.note}
          {k.spare.length > 0 ? ` This post will not save while ${k.spare.join(", ")} ${k.spare.length === 1 ? "is" : "are"} in the cell. Remove ${k.spare.length === 1 ? "it, or give it an article of its own" : "them, or give them an article of their own"}.` : ""}
        </p>
      </div>
    );
  }

  // ---- right drawer: full single-item editor ----
  //
  // TWO DRAWERS IN ONE, because the verb is the row's. A row that can be
  // written gets the editor it has always had. A row that cannot gets the same
  // values, read only, the endpoint's own sentence for why, and the one action
  // that is real there: the brief on the clipboard. It is not a disabled
  // version of the editor, because a disabled form invites somebody to look for
  // the permission that would enable it, and there is none to find.
  function MetaDrawer({ row, onClose, onChange, onSave, saving, blockedNote, siteId, budget, onCopyBrief }) {
    const DS = window.HuddleDesignSystem_775113; const { Drawer, Button, Field, Input, Textarea } = DS;
    const [aiBusy, setAiBusy] = useState(false);
    const [aiErr, setAiErr] = useState(null);
    const [aiSpare, setAiSpare] = useState([]);
    const kw = readKeywords(row.kw);
    // The drawer is opened from a row, so the row's own budget travels with it.
    // Defaulting here rather than at the call site means a future caller that
    // forgets the prop gets the plain 60 instead of a crash.
    const bud = budget || titleBudget("");
    const aiFill = async () => {
      setAiBusy(true); setAiErr(null); setAiSpare([]);
      try {
        const r = await api(`/claude/seo`, { method: "POST", body: JSON.stringify({ title: row.title, focusKeyword: kw.primary, site_id: siteId }) });
        const seo = (r && r.seo) || {};
        if (seo.titleTag) onChange("t", seo.titleTag);
        if (seo.metaDescription) onChange("d", seo.metaDescription);
        // The keywords cell holds words a person typed, so a fill adds to it and
        // never rewrites it: everything already there is kept, in order, and the
        // suggestions take whatever room the rule leaves. What does not fit is
        // named below instead of being appended for the save to then refuse.
        const primary = kw.primary || String(seo.focusKeyword || "").trim();
        if (primary) {
          const held = kw.secondary.concat(kw.spare);
          const isHeld = (k) => held.some(h => h.toLowerCase() === k.toLowerCase());
          const suggested = normaliseKeywords(Array.isArray(seo.secondaryKeywords) ? seo.secondaryKeywords : String(seo.secondaryKeywords || "").split(","));
          const room = capSecondary(held.concat(suggested), primary);
          onChange("kw", [primary].concat(held, room.secondary.filter(k => !isHeld(k))).join(", "));
          setAiSpare(room.spare.filter(k => !isHeld(k)));
        }
      } catch (e) { setAiErr((e && e.message) || "AI Fill failed"); }
      finally { setAiBusy(false); }
    };
    const sectionLine = row.sectionName
      ? <div className="bm-kw">Section: <b>{row.sectionName}</b>{row.readFrom === "crawl" ? ", read by crawling the public page" : ", read through its own CMS"}</div>
      : null;
    const preview = (
      <div>
        <div className="bi-field__label">Search preview</div>
        <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: 14, background: "var(--bg-card)" }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{(() => { try { return new URL(row.link).host; } catch { return "your-site"; } })()} › …</div>
          {/* The suffix is shown in the preview for the same reason it is
              subtracted from the counter: a preview of a title the site does
              not actually serve is a preview of the wrong page. */}
          <div style={{ fontSize: 18, color: "#1a0dab", lineHeight: 1.3, margin: "2px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(row.t || row.title) + (bud.suffix || "")}</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>{row.d || "No meta description set - Google will generate one from the page content."}</div>
        </div>
      </div>
    );

    // The read-only half. Every value the editor would hold, shown as what the
    // page serves, with the one verb that exists here in the footer.
    if (!row.canSave) {
      return React.createElement(Drawer, { title: row.title, subtitle: "Read by crawling, with no CMS behind it", onClose,
        footer: <><Button variant="ghost" onClick={onClose}>Close</Button><Button variant="primary" onClick={() => onCopyBrief && onCopyBrief()}>Copy the handover brief</Button></> }, (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{statusBadge(row.status)}{row.link ? <a className="bm-post__view" href={row.link} target="_blank" rel="noopener noreferrer">View live <I name="external-link" size={12} /></a> : null}</div>
          {sectionLine}
          {/* VERBATIM. The sentence is the endpoint's, and rewording it here is
              how two screens come to give a customer two different reasons for
              the same fact. */}
          {React.createElement(DS.Banner, { tone: "info", title: "There is nothing here to save" }, row.verbWhy)}
          <div>
            <div className="bi-field__label">Title this page serves</div>
            <p className="bm-kw-note">{row.t || "(none)"}</p>
            {counter(row.t.length, bud.limit, bud)}
            {bud.suffix ? <p className="bm-kw-note" style={{ marginTop: 4 }}>{suffixNote(bud)}</p> : null}
          </div>
          <div>
            <div className="bi-field__label">Description this page serves</div>
            <p className="bm-kw-note">{row.d || "(none)"}</p>
            {counter(row.d.length, DESC_LIMIT)}
          </div>
          <p className="bm-kw-note">Keywords are a CMS field, and this page has no CMS behind it, so there is nowhere to store one. The brief carries the URL, both values as they are served now, and what has to fit.</p>
          {preview}
        </div>
      ));
    }

    return React.createElement(Drawer, { title: row.title, subtitle: "Edit on-page SEO", onClose,
      footer: <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={saving || !!blockedNote} onClick={() => onSave && onSave()}>{saving ? "Saving…" : "Save changes"}</Button></> }, (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{statusBadge(row.status)}{row.link ? <a className="bm-post__view" href={row.link} target="_blank" rel="noopener noreferrer">View live <I name="external-link" size={12} /></a> : null}</div>
        {sectionLine}
        {blockedNote && React.createElement(DS.Banner, { tone: "danger", title: "Saving is held" }, blockedNote)}
        <Field label="SEO title" hint={bud.suffix ? `Aim for ${bud.limit} characters or fewer.` : `Aim for ${TITLE_LIMIT - 10}-${TITLE_LIMIT} characters.`}><Input value={row.t} onChange={e => onChange("t", e.target.value)} placeholder="Add an SEO title" /></Field>
        {/* The drawer has room for the whole sentence, so it gets it rather
            than the tooltip the cramped table cell has to make do with. */}
        <div style={{ marginTop: -10 }}>
          {counter(row.t.length, bud.limit, bud)}
          {bud.suffix ? <p className="bm-kw-note" style={{ marginTop: 4 }}>{suffixNote(bud)}</p> : null}
        </div>
        <Field label="Meta description" hint={`Aim for ${DESC_LIMIT - 10}-${DESC_LIMIT} characters.`}><Textarea rows={3} value={row.d} onChange={e => onChange("d", e.target.value)} placeholder="Add a meta description" /></Field>
        <div style={{ marginTop: -10 }}>{counter(row.d.length, DESC_LIMIT)}</div>
        <Field label="Keywords" hint={RULE_SENTENCE + " Comma separated, strongest first."}><Input value={row.kw} onChange={e => onChange("kw", e.target.value)} leadingIcon="search" placeholder="primary keyword, then up to three secondaries" /></Field>
        <div style={{ marginTop: -10 }}>{kwPanel(kw)}</div>
        <button className="bi-btn bi-btn--ai" style={{ alignSelf: "flex-start" }} onClick={aiFill} disabled={aiBusy}><I name="sparkles" size={16} />{aiBusy ? "Filling…" : "AI Fill SEO"}</button>
        {aiSpare.length > 0 && (
          <p className="bm-kw-note">AI Fill also suggested {aiSpare.join(", ")}. This post already targets {MAX_SECONDARY} secondary keywords, so {aiSpare.length === 1 ? "it was" : "they were"} not added. {aiSpare.length === 1 ? "It is" : "They are"} usually a separate article rather than a fourth section of this one.</p>
        )}
        {aiErr && React.createElement(DS.Banner, { tone: "danger", title: "AI Fill failed" }, aiErr)}
        {/* live SERP preview */}
        {preview}
      </div>
    ));
  }

  // ---- a whole row opens the drawer, without swallowing what is inside it ----
  //
  // Two ways to do this: stop propagation on every control in the row, or ask
  // the event where it came from. This asks, for two reasons. The per-control
  // way is only correct until the next control is added and nobody remembers to
  // add the stopPropagation, and it fails silently when it is forgotten. And a
  // row holds markup this file does not own (the DS check box, anything a DS
  // component renders inside itself), which cannot be given a handler from here.
  //
  // The selector is deliberately wider than the controls a row has today:
  // `select` and `[role="button"]` are not in a row yet, and the guard should
  // already be right on the day they are.
  const ROW_CONTROLS = 'a, button, input, textarea, select, label, [role="button"], [contenteditable="true"]';
  function fromRowControl(e) {
    const t = e.target;
    /* The match must stop BELOW the row, and this is not a nicety: the row
       itself carries role="button" so it can be reached by keyboard, and
       `[role="button"]` is in the selector above. A plain closest() therefore
       walks up from any cell, matches the ROW, and reports every click as
       coming from a control, so the drawer never opens at all. Comparing
       against currentTarget is what keeps the row out of its own guard.
       Found by clicking a plain cell in a browser: the source reads correctly
       and the tests, which assert over source text, cannot see it. */
    if (t && typeof t.closest === "function") {
      const hit = t.closest(ROW_CONTROLS);
      if (hit && hit !== e.currentTarget) return true;
    }
    // A click that ends a text selection is the end of a drag, not a click on
    // the row. Opening the drawer there would snatch away the cell text
    // somebody was part way through copying.
    const picked = typeof window.getSelection === "function" ? window.getSelection() : null;
    return !!(picked && String(picked).length > 0);
  }

  /* The screen takes the switcher's two pieces of state and they are not the
     same thing (main.jsx says the same where they are made): `site` is the
     ANCHOR CONNECTION, which every request still carries so the server can
     authorise it and derive the property itself; `sectionId` is the FILTER,
     null at All and a section's own site_id otherwise. `sections` is this
     property's pieces, and this screen needs it for one thing above all: the
     ADDRESS a row's save must be sent to. */
  function BulkMeta({ site, sectionId, sections }) {
    const rawUrl = (site && (site.urlFull || site.url)) || "";
    const fullUrl = rawUrl.startsWith("http") ? rawUrl : (rawUrl ? "https://" + rawUrl : "");
    const enc = encodeURIComponent(fullUrl);
    // Read once here rather than at each call site: the shell hands the site
    // over as site_id on one route and id on another, and the bulk fill and
    // the drawer both need it.
    const siteId = site ? (site.site_id != null ? site.site_id : site.id) : null;
    const siteName = (site && site.name) || "";
    // The All position: no section named, so the subject is the whole website.
    const atAll = sectionId == null;
    const sectionList = Array.isArray(sections) ? sections : [];
    // At All a template's {site} cannot be a different brand per row, and the
    // anchor's name is one section's name rather than the website's, so the
    // website's own name is used and the help text says which.
    const tplSiteName = (atAll ? ((site && site.property_name) || siteName) : siteName) || "";
    const [tab, setTab] = useState("posts");
    const [data, setData] = useState({ posts: [], pages: [] });
    const [orig, setOrig] = useState({ posts: [], pages: [] });
    // What the property read said about itself, per tab: the sections it asked,
    // why they are the ones it asked, why there is no single budget, and where
    // the answer came from. Null on the per section read, which says none of it.
    const [meta, setMeta] = useState({ posts: null, pages: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingWhat, setSavingWhat] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [drawerId, setDrawerId] = useState(null);

    /* An address per section, which is the single most consequential lookup on
       this screen. A post id is meaningful only inside the CMS it came from, so
       a PATCH carrying one section's post id and ANOTHER section's address
       writes a title onto whatever post happens to hold that id in the wrong
       website. Nothing in the response can be used for this: the endpoint sends
       a section's id and name, not its address, so the address comes off the
       `sections` prop the shell already holds. A row whose section is not in
       that prop gets NO address and its save is held and named, rather than
       falling back to the anchor's, which is the defect this exists to stop. */
    const addressOf = (secId) => {
      const s = sectionList.find(x => (x.site_id != null ? x.site_id : x.id) === Number(secId));
      const raw = (s && (s.urlFull || s.url)) || "";
      return raw.startsWith("http") ? raw : (raw ? "https://" + raw : "");
    };
    const urlOf = (r) => (atAll && r.sectionId != null ? addressOf(r.sectionId) : fullUrl);
    const sectionNameOf = (secId) => {
      const s = sectionList.find(x => (x.site_id != null ? x.site_id : x.id) === Number(secId));
      return (s && s.name) || "";
    };

    const load = useCallback(async () => {
      setLoading(true);
      try {
        if (atAll) {
          // One read per tab, merged the way this screen has always merged its
          // two reads. A tab that fails keeps its own failure: Posts being
          // unreadable is not a reason to empty Pages.
          if (siteId == null) { setData({ posts: [], pages: [] }); setOrig({ posts: [], pages: [] }); setMeta({ posts: null, pages: null }); return; }
          const blank = (e) => ({ items: [], sections: [], sections_why: "", budget_why: "", scope: null, error: (e && e.message) || "could not be read" });
          const [p, pg] = await Promise.all([
            api(`/wp/bulk-meta-property?site_id=${siteId}&type=posts&perPage=100`).catch(blank),
            api(`/wp/bulk-meta-property?site_id=${siteId}&type=pages&perPage=100`).catch(blank),
          ]);
          const take = (r) => ({
            sections: Array.isArray(r && r.sections) ? r.sections : [],
            sections_why: (r && r.sections_why) || "",
            budget_why: (r && r.budget_why) || "",
            scope: (r && r.scope) || null,
            error: (r && r.error) || null,
          });
          const posts = ((p && p.items) || []).map(mapBM); const pages = ((pg && pg.items) || []).map(mapBM);
          setData({ posts, pages }); setOrig(JSON.parse(JSON.stringify({ posts, pages })));
          setMeta({ posts: take(p), pages: take(pg) });
          return;
        }
        if (!fullUrl) { setData({ posts: [], pages: [] }); setOrig({ posts: [], pages: [] }); setMeta({ posts: null, pages: null }); return; }
        const [p, pg] = await Promise.all([
          api(`/wp/bulk-meta?siteUrl=${enc}&type=posts&perPage=100`).catch(() => ({ items: [] })),
          api(`/wp/bulk-meta?siteUrl=${enc}&type=pages&perPage=100`).catch(() => ({ items: [] })),
        ]);
        const posts = ((p && p.items) || []).map(mapBM); const pages = ((pg && pg.items) || []).map(mapBM);
        setData({ posts, pages }); setOrig(JSON.parse(JSON.stringify({ posts, pages }))); setMeta({ posts: null, pages: null });
      } catch { setData({ posts: [], pages: [] }); }
      finally { setLoading(false); }
    }, [atAll, siteId, fullUrl, enc]);
    useEffect(() => { load(); }, [load]);
    // Switching position changes which rows exist and what their ids mean, so
    // anything keyed on a row id is dropped rather than carried across.
    useEffect(() => { setSel([]); setDrawerId(null); }, [sectionId]);

    /* ── The section's title suffix, read on its own and never blocking ──
       This is a SECOND effect rather than another leg of load()'s
       Promise.all, and that is the whole safety of it. The table is the screen;
       the suffix only changes a number printed beside it. Folded into load()
       a site with no stored audit, a route that 500s, or a slow crawl table
       would delay or empty the thing somebody came here to edit, to deliver a
       refinement they did not ask for. So it arrives late, or never, and the
       screen behaves exactly as it did before either way.

       `source=public` is asked for explicitly: the suffix is a fact about
       rendered HTML, and the REST lens never sees a served <title>, so
       accepting whichever snapshot exists would silently read the wrong one.

       IT DOES NOT RUN AT ALL, and that is the point of the guard rather than an
       optimisation. A suffix belongs to one front end. Reading the anchor's and
       printing it beside every row would hand the marketing site's budget to
       the blog's titles, which is the original bug of this screen wearing a
       property shaped hat. At All the suffix is on the ROW. */
    const [titleSuffix, setTitleSuffix] = useState("");
    useEffect(() => {
      if (atAll || !fullUrl) { setTitleSuffix(""); return; }
      let alive = true;
      api(`/wp/audit-latest?siteUrl=${enc}&source=public`, { silent401: true })
        .then(r => {
          if (!alive) return;
          // Named `am` rather than `meta` because this screen now holds a
          // `meta` of its own, and a shadow here would read as the property
          // answer while being one site's audit.
          const am = (r && r.audit && r.audit.meta) || {};
          // Absent, null, or a non-string from an older snapshot all mean the
          // same thing here: this site has no known convention, so 60 stands.
          setTitleSuffix(typeof am.title_suffix === "string" ? am.title_suffix : "");
        })
        .catch(() => { if (alive) setTitleSuffix(""); });
      return () => { alive = false; };
    }, [atAll, fullUrl, enc]);
    // The one budget the per section position has, and nothing reads it at All.
    const sectionBudget = titleBudget(titleSuffix);
    /* THE BUDGET IS PER ROW. At a section every row shares the one suffix that
       section's own front end appends, which is what the audit read above
       found. At All the suffix came down ON the row, from that section's own
       snapshot, so this is the only function on the screen that may be asked
       what a title may weigh. */
    const budgetOf = (r) => (atAll ? titleBudget(r && r.suffix) : sectionBudget);

    const rows = data[tab];
    const origRows = orig[tab];
    const tabMeta = meta[tab];
    const isDirty = (r) => { const o = origRows.find(x => x.id === r.id); return o && (o.t !== r.t || o.d !== r.d || o.kw !== r.kw); };
    const allDirty = () => data.posts.concat(data.pages).filter(r => { const all = orig.posts.concat(orig.pages); const o = all.find(x => x.id === r.id); return o && (o.t !== r.t || o.d !== r.d || o.kw !== r.kw); });
    const dirtyCount = allDirty().length;

    // Over the cap is refused, never trimmed. capSecondary would happily hand
    // back three and put the rest in `spare`, but writing that back would
    // delete words somebody typed: in this screen the terms live in one text
    // cell, so a save that "fixed" the row would be exactly the silent drop the
    // rule exists to prevent. The save is held instead, the extra terms are
    // named, and the person decides which article they belong to.
    const blocked = allDirty().map(r => ({ row: r, kw: readKeywords(r.kw) })).filter(x => !x.kw.advice.ok);

    const upd = (id, field, v) => setData(d => ({ ...d, [tab]: d[tab].map(r => r.id === id ? { ...r, [field]: v } : r) }));

    /* ── The bulk bar's buttons, which act on what CAN be written ──
       This screen is the one a person opens to fix on-page SEO at scale, and
       its bulk bar shipped with three controls that had no onClick between
       them. They enabled themselves the moment a row was ticked, so AI Fill
       SEO looked ready, was pressed, and silently did nothing. Found with 90
       posts selected and 228 empty fields to fill, which is exactly the
       situation the bar exists for.

       What they do now, and what they deliberately do not:

       The three writing buttons act on the ticked rows that CAN be written and
       NAME the ones they held back. Silently skipping a crawled row would be
       worse than refusing the whole press: somebody would believe the whole
       selection had been worked on. The rows that were held have their own
       bulk verb beside them, which is the brief.

       The fill never touches the KEYWORDS cell. A title and a description are
       written from what the page is about; a focus keyword is a decision about
       what it should RANK for, which comes from keyword research the owner
       already has. A model guessing one writes something plausible that
       quietly competes with their real targeting, so the cell is left for
       them to import into.

       The fill never overwrites a value that is already there. Somebody wrote
       it, or an earlier pass did, and replacing it without being asked is the
       one outcome worse than leaving the field empty.

       Nothing is saved. The values land in the row inputs as unsaved edits,
       the dirty count goes up, and Save is still a separate decision,
       exactly as it is when you type them by hand. */
    // Thirty, because POST /claude/rewrite-meta takes thirty items per call
    // (REWRITE_CAP on the route) and SILENTLY slices the rest off the end.
    // Raising this number alone would not widen the pass, it would make rows
    // disappear from the result with nothing said, so the two have to agree.
    // At All it is thirty PER SECTION, because a section is a call: see the
    // grouping below for why there cannot be one call for the website.
    const AI_FILL_CAP = 30;
    const [bulkBusy, setBulkBusy] = useState(null);   // { phase, total } while working
    const [bulkNote, setBulkNote] = useState(null);

    // Names, capped. A banner listing ninety titles is a banner nobody reads,
    // and a bare count with no names is the silent miss this file keeps
    // refusing to ship, so it is a few names and then a number.
    const nameList = (titles, cap = 5) => titles.slice(0, cap).map(t => `"${t}"`).join(", ")
      + (titles.length > cap ? ` and ${titles.length - cap} more` : "");

    /* Two words that change with the position, and nothing else in these
       receipts does. At one section every row is writable and the one Save is
       still called "Save changes", so every sentence below reads exactly as it
       read before this screen learned about the website. */
    const saveWord = atAll ? "Save" : "Save changes";
    const pickedWord = atAll ? "writable selected" : "selected";

    // The ticked rows, split by what can be done to them. Every bulk action
    // starts here, so no action can forget the second half.
    const pickedRows = () => rows.filter(r => sel.includes(r.id));
    const heldText = (held) => {
      if (!held.length) return "";
      const names = Array.from(new Set(held.map(r => r.sectionName).filter(Boolean)));
      return `${held.length} ticked row(s) were held back because there is nothing to write to: ${nameList(held.map(r => r.title))}.`
        + (names.length ? ` ${names.join(" and ")} ${names.length === 1 ? "is" : "are"} read by crawling, with no CMS behind ${names.length === 1 ? "it" : "them"}.` : "")
        + " The verb on those rows is Copy the handover brief.";
    };

    /* One group per section, which is what the write and the budget both force.
       The fill sends ONE call per group for three separate reasons, and any one
       of them would be enough: a call carries a single max_title and a budget
       is per section; the route correlates its answers by post_id alone, so two
       sections holding the same id in one call would swap each other's titles;
       and the excerpts come from a route that takes one site's address. */
    const groupsOf = (list) => {
      const out = [];
      for (const r of list) {
        const key = r.sectionId == null ? "anchor" : String(r.sectionId);
        let g = out.find(x => x.key === key);
        if (!g) {
          g = { key, name: r.sectionName || sectionNameOf(r.sectionId) || siteName, url: urlOf(r), budget: budgetOf(r), rows: [] };
          out.push(g);
        }
        g.rows.push(r);
      }
      return out;
    };

    /* ── The excerpts, which are the whole reason this changed ──
       The fill used to POST /claude/seo once per row with nothing but
       `{ title }`, so every description on this screen was written from a
       HEADLINE. That is guesswork dressed as content: "5 ways to cut your
       build time" tells a model the shape of a sentence and nothing about the
       page, and the descriptions came back interchangeable because they were.

       /wp/bulk-meta, which fills this table, does not return excerpts. The
       route that does is /wp/posts, whose _fields include it, so the excerpts
       are fetched separately and handed to the writer with the headline. It
       takes ONE site's address, which is why this is called per group: at All
       the posts of two sections live in two different CMSs.

       THREE things this has to tolerate, and does.

       status=any is asked for first because this screen lists drafts and posts
       in review (its own list is fetched that way) while /wp/posts defaults to
       published only: without it the rows most likely to have no description
       yet would be exactly the ones that came back with no excerpt. A site
       connected without credentials cannot ask WordPress for a non-public
       status at all, and that is a refusal rather than a shorter list, so the
       published-only read is the fallback.

       The two routes order differently: bulk-meta serves the hundred most
       recently MODIFIED, /wp/posts answers by date. So the id we want can sit
       past the first page, which is why this walks pages instead of reading
       one. Five pages is the ceiling, and a row still not found is sent with
       an empty excerpt rather than dropped from the batch.

       PAGES have no excerpt anywhere this screen may read: /wp/pages does not
       request the field and there is no batched route that does. A page is
       therefore described from its headline, as before, and the receipt says
       so instead of letting the result look the same as a post's.

       The HTML is left alone on purpose. WordPress returns the excerpt as
       rendered HTML and the route strips the tags itself, so stripping them
       here too would be a second implementation of the same rule, drifting.

       The map is keyed by the ROW's identity rather than by a post id, because
       at All a post id is not unique across the website. */
    const excerptsFor = async (needed, url) => {
      const out = new Map();
      if (!needed.length || tab !== "posts" || !url) return out;
      const byPost = new Map(needed.map(r => [Number(r.postId), r]));
      const q = encodeURIComponent(url);
      const read = async (statusQs) => {
        const got = await apiList(`/wp/posts?siteUrl=${q}${statusQs}`, { maxPages: 5 });
        return (got && got.items) || [];
      };
      try {
        let list;
        try { list = await read("&status=any"); } catch { list = await read(""); }
        for (const p of list) {
          const r = byPost.get(Number(p && p.id));
          if (!r) continue;
          const raw = p.excerpt && typeof p.excerpt === "object" ? p.excerpt.rendered : p.excerpt;
          const text = String(raw || "").trim();
          if (text) out.set(r.id, text);
        }
      } catch { /* No excerpts at all. The fill still runs, thinner, and says so. */ }
      return out;
    };

    const aiFillSelected = async () => {
      const picked = pickedRows();
      const writable = picked.filter(r => r.canSave);
      const heldRows = picked.filter(r => !r.canSave);
      if (!writable.length) {
        setBulkNote({ tone: "info", text: `Nothing ticked can be written, so nothing was filled. ${heldText(heldRows)}`.trim() });
        return;
      }
      const todo = writable.filter(r => !String(r.t || "").trim() || !String(r.d || "").trim());
      if (!todo.length) {
        setBulkNote({ tone: "info", text: `All ${writable.length} ${pickedWord} row(s) already have a title and a description. Nothing was changed: clear a field first if you want it written again. ${heldText(heldRows)}`.trim() });
        return;
      }
      const groups = groupsOf(todo).map(g => ({ ...g, batch: g.rows.slice(0, AI_FILL_CAP) }));
      const batchTotal = groups.reduce((n, g) => n + g.batch.length, 0);
      setBulkNote(null);
      /* The progress indicator stopped being "done of total" because there is
         nothing honest to count any more: thirty rows are now ONE request, and
         a counter that sat at 0 and jumped to 30 would be an animation
         pretending to be progress. What it reports instead is the phase, which
         is real: the excerpts are read, then the batch is written. At All there
         is one more real thing to report, which is which section is in flight. */
      let filled = 0;
      const refusedLines = [];
      const silent = [];
      const thin = [];
      const failedGroups = [];
      const doneGroups = [];

      for (const g of groups) {
        const where = groups.length > 1 ? ` in ${g.name}` : "";
        setBulkBusy({ phase: "excerpts", total: g.batch.length, where });
        const excerpts = await excerptsFor(g.batch, g.url);
        for (const r of g.batch) if (!excerpts.get(r.id)) thin.push(r);

        /* One item per row, and `need` is what makes the second rule structural
           rather than remembered. A field with something in it is NEVER named in
           `need`, and the route's own sorter drops any value returned for a field
           the caller did not name, so there is no path by which this call can
           come back holding a replacement for a title somebody wrote.

           The stored values still travel, as CONTEXT. A post with a title and no
           description gets its title sent so the writer can see what the page is
           called, which is exactly the case the route documents for its `need`
           narrowing. Sending nothing would make the description worse to protect
           against a risk the contract already removes.

           `need` is never empty here: `todo` only holds rows with at least one
           field missing. That matters because an empty `need` means something
           else entirely to the route (shorten everything you were sent), and
           this screen must never ask for that. */
        const items = g.batch.map(r => {
          const t = String(r.t || "").trim();
          const d = String(r.d || "").trim();
          const need = [];
          if (!t) need.push("title");
          if (!d) need.push("meta_description");
          return {
            post_id: r.postId, need,
            post_title: r.title,
            excerpt: excerpts.get(r.id) || "",
            url: r.link || "",
            title: t, meta_description: d,
          };
        });

        setBulkBusy({ phase: "writing", total: g.batch.length, where });
        try {
          /* max_title is THIS SECTION'S BUDGET, not 60 and not the website's,
             so a front end that appends a suffix gets titles that still fit
             once it has. The route clamps the value to 30 at the bottom, which
             is the same floor MIN_TITLE_BUDGET keeps the counter at, so the two
             cannot disagree about what was asked for. max_description is the
             target under the displayed 160, for the headroom reason given at
             DESC_TARGET. */
          const res = await api(`/claude/rewrite-meta`, { method: "POST", body: JSON.stringify({
            items, site_id: siteId, max_title: g.budget.limit, max_description: DESC_TARGET,
          }) });
          // Correlation is by post id INSIDE one group, which is the only place
          // a post id is unique: one group is one CMS.
          const byId = new Map(g.batch.map(r => [Number(r.postId), r]));
          const refused = Array.isArray(res && res.refused) ? res.refused : [];
          const landed = new Set();
          for (const s of (Array.isArray(res && res.suggestions) ? res.suggestions : [])) {
            const r = byId.get(Number(s && s.post_id));
            if (!r) continue;
            // The emptiness is checked again here, on our side of the wire. The
            // route honours `need` and this is the same rule stated twice on
            // purpose: it is the one promise whose breach destroys somebody's
            // work, and a second guard costs two comparisons.
            let touched = false;
            if (!String(r.t || "").trim() && typeof s.title_tag === "string" && s.title_tag.trim()) { upd(r.id, "t", s.title_tag.trim()); touched = true; }
            if (!String(r.d || "").trim() && typeof s.meta_description === "string" && s.meta_description.trim()) { upd(r.id, "d", s.meta_description.trim()); touched = true; }
            if (touched) { filled += 1; landed.add(r.id); }
          }
          // A refusal is a value the route threw away for still being over the
          // limit after its retry. It has to be NAMED: the field is untouched on
          // the live site, and a row that quietly came back empty looks identical
          // to a row the model simply had nothing to say about.
          for (const x of refused) {
            const r = byId.get(Number(x && x.post_id));
            const what = x && x.field === "title" ? "title" : "description";
            refusedLines.push(`${r ? `"${r.title}"` : `post ${x && x.post_id}`}${where} (${what}: ${(x && x.why) || "over the limit"})`);
          }
          const refusedIds = new Set(refused.map(x => {
            const r = byId.get(Number(x && x.post_id));
            return r ? r.id : null;
          }));
          for (const r of g.batch) if (!landed.has(r.id) && !refusedIds.has(r.id)) silent.push(r);
          doneGroups.push(g);
        } catch (e) {
          // ONE SECTION FAILING IS NOT THE WEBSITE FAILING, which is the same
          // stance the endpoint takes on a CMS it could not read. The other
          // sections' rows are still filled, and the one that threw is named
          // with its reason rather than folded into a total.
          failedGroups.push({ group: g, why: (e && e.message) || "the AI call failed" });
        }
      }
      setBulkBusy(null);

      if (!doneGroups.length) {
        // One call per section is cheaper and better, and this is its one cost,
        // said plainly: when a section's call fails it fails for every row in
        // that section. Nothing partial was written, so pressing again is safe.
        const why = failedGroups.map(f => (groups.length > 1 ? `${f.group.name}: ${f.why}` : f.why)).join("; ");
        setBulkNote({ tone: "error", text: `${why} ${groups.length > 1 ? "A section's selection" : "The whole selection"} goes in one call, so nothing at all was filled and nothing was changed in the table. Pressing AI Fill SEO again is safe. ${heldText(heldRows)}`.trim() });
        return;
      }

      const left = todo.length - batchTotal;
      const budgetLine = groups.length > 1
        ? `Written from each post's own headline and excerpt, one call per section, against each section's own title limit: ${groups.map(g => `${g.name} at ${g.budget.limit}`).join(", ")}. A website with two front ends has two budgets, so there is no single number here.`
        : `Written from each post's own headline and excerpt in one call, against a ${groups[0].budget.limit} character title limit${groups[0].budget.suffix ? ` (${TITLE_LIMIT} less the "${groups[0].budget.suffix}" this site appends to every title)` : ""}.`;
      setBulkNote({
        tone: (refusedLines.length || silent.length || failedGroups.length || heldRows.length) ? "warning" : "success",
        text: [
          `${filled} row(s) filled and waiting in the table. Nothing is saved until you press ${saveWord}.`,
          budgetLine,
          thin.length ? `${thin.length} of them had no excerpt to work from, so ${thin.length === 1 ? "its description is" : "their descriptions are"} thinner and more general than the rest: ${nameList(thin.map(r => r.title))}. An excerpt on the post is what makes that description specific.` : "",
          tab !== "posts" ? "Pages carry no excerpt in the API this screen reads, so a page is described from its headline alone." : "",
          refusedLines.length ? `Refused and NOT filled: ${refusedLines.join("; ")}. Those fields are untouched, and they need a shorter wording you choose rather than a third attempt.` : "",
          silent.length ? `${silent.length} row(s) came back with nothing at all and were left exactly as they were: ${nameList(silent.map(r => r.title))}. A second press is worth trying on those.` : "",
          failedGroups.length ? `${failedGroups.map(f => `${f.group.name}'s call failed (${f.why}), so its ${f.group.batch.length} row(s) are unchanged`).join("; ")}. Pressing again retries only what is still empty.` : "",
          left ? `${left} more selected row(s) were left for a second pass: ${AI_FILL_CAP} at a time${groups.length > 1 ? " per section" : ""} is both a bound on what one press can spend and all one call accepts.` : "",
          "Keywords were not touched.",
          heldText(heldRows),
        ].filter(Boolean).join(" "),
      });
    };

    const clearMetaSelected = () => {
      const picked = pickedRows();
      const writable = picked.filter(r => r.canSave);
      const heldRows = picked.filter(r => !r.canSave);
      const hadSomething = writable.filter(r => String(r.t || "").trim() || String(r.d || "").trim());
      for (const r of hadSomething) { upd(r.id, "t", ""); upd(r.id, "d", ""); }
      setBulkNote(hadSomething.length
        ? { tone: "warning", text: `Cleared the title and description on ${hadSomething.length} row(s), in the table only. Press ${saveWord} to write the clearing to the live site, or Reload to put them back. Keywords were not touched. ${heldText(heldRows)}`.trim() }
        : { tone: "info", text: `Those rows had no title or description to clear. ${heldText(heldRows)}`.trim() });
    };

    /* ── Apply template, built rather than promised ────────────
       This button shipped once with no handler at all, and was then taken out
       of the bar rather than left looking ready: a template needs a pattern to
       apply and somewhere to type it, and neither existed. Both exist now, and
       the shape is deliberately the smallest honest one.

       It obeys the same rules the fill does. It writes ONLY the SEO title, so
       the keywords cell is not in its reach at all. It fills only the rows
       whose title is still empty, because a template is a convenience and the
       thing somebody typed by hand outranks it. It writes into the row inputs,
       so Save is still the decision. And it applies only to rows that can be
       written, naming the ones it held back.

       Over the budget is REFUSED, for every row, not truncated for the row
       that overflows. Truncating would cut the end off a headline somebody
       wrote and save it to a live site, and doing it to some rows and not
       others would leave a table where nobody can tell which titles are the
       pattern and which are a clipped version of it. All of the selected rows
       or none, and the ones that overflow are named with their lengths AND
       with the limit they broke, which at All is their own section's and not
       one number for the website. This is the same stance the keyword save
       takes a few lines up, for the same reason. */
    const [tplOpen, setTplOpen] = useState(false);
    const [tpl, setTpl] = useState("{title} | {site}");

    const applyTemplateSelected = () => {
      const picked = pickedRows();
      const writable = picked.filter(r => r.canSave);
      const heldRows = picked.filter(r => !r.canSave);
      const pattern = String(tpl || "").trim();
      if (!picked.length || !pattern) {
        setBulkNote({ tone: "info", text: "Type a pattern first. {title} is the post's own headline and {site} is the site name, so {title} | {site} is the usual one." });
        return;
      }
      if (!writable.length) {
        setBulkNote({ tone: "info", text: `Nothing ticked can be written, so no title was applied. ${heldText(heldRows)}`.trim() });
        return;
      }
      // A pattern with no {title} gives every selected row the SAME SEO title.
      // Duplicate titles across a site are an error the Site Audit reports, so
      // one press here would manufacture ninety of them. Refused, not warned
      // about: there is no version of this the user meant.
      if (!/\{title\}/.test(pattern)) {
        setBulkNote({ tone: "warning", text: `That pattern has no {title} in it, so all ${writable.length} ${pickedWord} row(s) would end up with an identical SEO title. Duplicate titles across a site are an error the Site Audit reports, so nothing was applied.` });
        return;
      }
      const open = writable.filter(r => !String(r.t || "").trim());
      const alreadyTitled = writable.length - open.length;
      if (!open.length) {
        setBulkNote({ tone: "info", text: `All ${writable.length} ${pickedWord} row(s) already have an SEO title, and a template never overwrites one. Clear the title on the rows you want re-titled first, then apply. ${heldText(heldRows)}`.trim() });
        return;
      }
      const out = open.map(r => ({ row: r, text: renderTemplate(pattern, r.title, tplSiteName), budget: budgetOf(r) }));
      // THE COMPARISON IS AGAINST THE ROW'S OWN BUDGET. At All one pattern can
      // fit the blog and overflow the marketing front end, because the two
      // append different suffixes, so a single limit here would refuse rows
      // that fit and apply rows that do not.
      const over = out.filter(x => x.text.length > x.budget.limit);
      const limits = Array.from(new Set(out.map(x => x.budget.limit))).sort((a, b) => a - b);
      if (over.length) {
        setBulkNote({ tone: "warning", text: [
          `Nothing was applied.`,
          atAll
            ? `${over.length} of the ${open.length} row(s) this pattern would write come out over the limit their own section leaves:`
            : `${over.length} of the ${open.length} row(s) this pattern would write come out over the ${sectionBudget.limit} character limit${sectionBudget.suffix ? ` this site leaves once it appends "${sectionBudget.suffix}"` : ""}:`,
          over.slice(0, 5).map(x => atAll
            ? `"${x.row.title}" at ${x.text.length} against ${x.budget.limit}${x.row.sectionName ? ` in ${x.row.sectionName}` : ""}`
            : `"${x.row.title}" at ${x.text.length}`).join(", ") + (over.length > 5 ? ` and ${over.length - 5} more` : "") + ".",
          limits.length > 1 ? `The selection spans ${limits.length} different budgets (${limits.join(", ")}), because a title suffix belongs to a front end and this website has more than one.` : "",
          `Nothing is truncated for you: cutting a headline mid word and saving it is worse than leaving the field empty. Shorten the pattern, or deselect those rows and apply to the rest.`,
        ].filter(Boolean).join(" ") });
        return;
      }
      for (const x of out) upd(x.row.id, "t", x.text);
      setBulkNote({ tone: heldRows.length ? "warning" : "success", text: [
        `Applied to ${out.length} row(s), in the table only. Nothing is saved until you press ${saveWord}.`,
        alreadyTitled ? `${alreadyTitled} selected row(s) already had an SEO title and were left alone: a template never overwrites one.` : "",
        `Longest result: ${Math.max.apply(null, out.map(x => x.text.length))} of ${limits.join(" and ")} characters.`,
        "Keywords were not touched.",
        heldText(heldRows),
      ].filter(Boolean).join(" ") });
    };
    // The preview reads the first selected row, which is the row at the top of
    // the table rather than the first one ticked: what somebody checks before
    // pressing is "does this pattern look right", and any real row answers it.
    // It prefers a writable row, because a template cannot be applied to a row
    // with nothing to write to and a preview of one would be a promise.
    const tplFirst = rows.find(r => sel.includes(r.id) && r.canSave) || rows.find(r => sel.includes(r.id)) || null;
    const tplPreview = tplFirst ? renderTemplate(tpl, tplFirst.title, tplSiteName) : "";
    const tplBudget = tplFirst ? budgetOf(tplFirst) : sectionBudget;
    const tplUnknown = unknownTokens(tpl);

    /* ── The clipboard, which is the verb where there is no CMS ──
       The brief is composed in the client (see handoverBrief) and this is the
       only thing that puts it anywhere. It reports through the same banner
       every other result on this screen uses, so there is one place a person
       looks to find out what just happened. */
    const copyText = async (text, ok) => {
      try {
        if (!navigator.clipboard) throw new Error("This browser will not give the page clipboard access.");
        await navigator.clipboard.writeText(text);
        setBulkNote({ tone: "copied", text: ok });
      } catch (e) {
        setBulkNote({ tone: "copy_failed", text: (e && e.message) || "Could not copy the brief." });
      }
    };
    const copyBrief = (r) => copyText(
      handoverBrief(r, budgetOf(r)),
      `The brief for "${r.title}" is on your clipboard. ${r.sectionName || "That section"} has no CMS behind it, so this change has to be made by whoever builds that part of the site.`);
    const copyBriefsSelected = () => {
      const held = pickedRows().filter(r => !r.canSave);
      if (!held.length) {
        setBulkNote({ tone: "info", text: "None of the ticked rows needs a brief: every one of them can be written from here, so the verb on those is Save." });
        return;
      }
      return copyText(
        held.map(r => handoverBrief(r, budgetOf(r))).join("\n\n----\n\n"),
        `${held.length} brief(s) are on your clipboard, one after another: ${nameList(held.map(r => r.title))}. Each one names its own URL, what it serves now and what has to fit.`);
    };

    /* ── Saving, which is per SECTION and never per website ────
       `onlySection` is the section whose edited rows this press writes, and at
       All there is no press that leaves it out. That is the "no save all" rule
       and it is not tidiness: a post id means nothing outside the CMS it came
       from, so one button walking a hundred edited rows across three
       connections is one wrong address away from writing a blog's title onto
       whatever post holds that id in another website. Nothing in this app can
       undo that from here.

       Every write takes the ROW'S OWN address. A row whose section is not in
       the sections prop has no address, and is held and named rather than
       falling back to the anchor's. */
    const save = async (onlySection) => {
      // At a section the anchor IS the write target, so no address is no save.
      // At All the address comes off the row and the anchor's is not used.
      if (!atAll && !fullUrl) return;
      // The buttons are already disabled here. This is the gate that matters: the
      // drawer calls save() too, and a refusal that only lives in a disabled
      // attribute is one prop away from not being a refusal at all.
      if (blocked.length) return;
      setSaving(true);
      setSavingWhat(onlySection === undefined ? null : sectionNameOf(onlySection));
      const dirty = allDirty().filter(r => onlySection === undefined || r.sectionId === onlySection);
      const ptype = (id) => (data.pages.some(r => r.id === id) ? "page" : "post");
      const wrong = [];
      const failed = [];
      let written = 0;
      // The whole cell goes to focus_keyword, verbatim. Sending only the primary
      // would read as tidier and would destroy every secondary on the row: the
      // sparse PATCH writes that one column and has nowhere else to put them, so
      // they would be gone on the next load with nothing said.
      for (const r of dirty) {
        const url = urlOf(r);
        if (!r.canSave || r.postId == null || !url) { wrong.push(r); continue; }
        try {
          await api(`/wp/seo-meta/${r.postId}`, { method: "PATCH", body: JSON.stringify({ siteUrl: url, post_type: ptype(r.id), title_tag: r.t, meta_description: r.d, focus_keyword: r.kw }) });
          written += 1;
        } catch (e) { failed.push({ row: r, why: (e && e.message) || "the write failed" }); }
      }
      try { await load(); } catch { /* the rows stay as they are, and the note below still says what happened */ }
      setSaving(false); setSavingWhat(null);
      // A save that half worked used to say nothing at all, which left a person
      // reloading to find out. Each outcome is named, with the row.
      if (wrong.length || failed.length) {
        setBulkNote({ tone: "save_failed", text: [
          `${written} row(s) saved.`,
          failed.length ? `${failed.length} were refused by their own site: ${failed.map(f => `"${f.row.title}" (${f.why})`).join("; ")}.` : "",
          wrong.length ? `${wrong.length} could not be sent at all, because this screen has no address for the section ${wrong.length === 1 ? "it belongs" : "they belong"} to: ${nameList(wrong.map(r => r.title))}. Nothing was sent to another section's site in their place, which is the only safe answer.` : "",
        ].filter(Boolean).join(" ") });
      } else if (written) {
        setBulkNote({ tone: "saved", text: `${written} row(s) saved${onlySection === undefined ? "" : ` in ${sectionNameOf(onlySection) || "that section"}`}.` });
      }
    };
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const allChecked = rows.length > 0 && sel.length === rows.length;
    const drawerRow = drawerId != null ? rows.find(r => r.id === drawerId) : null;
    const DS = window.HuddleDesignSystem_775113;

    // Save writes every edited row of a section at once, so a refusal anywhere
    // holds the drawer's Save as well. Naming the post that is holding it is
    // the difference between a disabled button and an explanation.
    const blockedNote = !blocked.length ? null
      : blocked.map(b => `"${b.row.title}" targets ${b.kw.advice.count} secondary keywords, ${b.kw.spare.length} of them past the ${MAX_SECONDARY} a post may have: ${b.kw.spare.join(", ")}.`).join(" ")
        + (atAll
          ? " Save writes every edited row of a section at once, so it is held until that is settled."
          : " Save writes every edited row at once, so it is held until that is settled.");

    // Which sections hold edited rows, in the order their rows appear, with a
    // live count. One Save button each, and no button for all of them.
    const dirtySections = (() => {
      const out = [];
      for (const r of allDirty()) {
        const key = r.sectionId == null ? null : r.sectionId;
        let g = out.find(x => x.key === key);
        if (!g) { g = { key, name: r.sectionName || sectionNameOf(key) || siteName, rows: 0, address: urlOf(r) }; out.push(g); }
        g.rows += 1;
      }
      return out;
    })();

    // The per section report, counted from the rows ON SCREEN rather than from
    // the response's own figure: a list capped for the read sets the ORDER, and
    // a count shown to a person has to come from live state.
    const sectionReport = (tabMeta && tabMeta.sections ? tabMeta.sections : []).map(s => ({
      ...s,
      live: rows.filter(r => r.sectionId === s.section_id).length,
      budget: titleBudget(s.title_suffix),
    }));
    const brokenSections = sectionReport.filter(s => s.error);
    const handoverCount = rows.filter(r => !r.canSave).length;
    const pickedHeldCount = rows.filter(r => sel.includes(r.id) && !r.canSave).length;
    const pickedWritableCount = sel.length - pickedHeldCount;

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles">
            <h1 className="bi-title"><span className="bi-title__icon"><I name="edit" size={20} /></span>Bulk Meta Editor</h1>
            <div className="bi-subtitle">On-page SEO at scale{atAll ? " · every piece of this website" : (site ? " · " + site.name : "")}</div>
          </div>
          <div className="bi-header-actions">
            <div className="kw-seg kw-seg--lg">{[["posts", "Posts"], ["pages", "Pages"]].map(([id, l]) => <button key={id} className={tab === id ? "on" : ""} onClick={() => { setTab(id); setSel([]); }}>{l}</button>)}</div>
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" onClick={load}><I name="refresh" size={16} />Reload</button>
            {/* ONE Save, and only where one address is the right one. At All it
                is replaced by one button per section, below. */}
            {!atAll && (
              <button className="bi-btn bi-btn--primary" disabled={!dirtyCount || saving || blocked.length > 0} onClick={() => save()}>{saving ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Saving…</> : <><I name="check" size={16} />Save changes{dirtyCount ? " (" + dirtyCount + ")" : ""}</>}</button>
            )}
          </div>
        </div>
        <p className="seo-intro">Edit inline, or click a row to open the full editor. Select rows for bulk actions. Edited rows highlight; Save shows the live dirty count. Keywords go in one cell, comma separated: <b>{RULE_SENTENCE}</b>
          {atAll ? <> Every row names the piece of the website it came from, and the verb is the row's: <b>Save</b> where a CMS can take it, <b>Copy the handover brief</b> where there is none. Saving is per piece, so there is no one button that writes all of them.</> : null}
        </p>

        {/* Said once at the top as well as in every counter's tooltip. A
            changed number in forty places is noticed; a changed number nobody
            explained is argued with. ONE suffix is only a true statement at one
            section; at All the same sentence appears per section in the report
            below, because a front end owns a suffix and a website can have
            several. */}
        {!atAll && sectionBudget.suffix ? (
          <p className="seo-intro bm-suffix-note">
            <I name="alert-triangle" size={14} /> Titles are counted to <b>{sectionBudget.limit}</b>, not {TITLE_LIMIT}: the last crawl found this site appending <b>{sectionBudget.suffix}</b> to every title it serves. {suffixNote(sectionBudget)}
          </p>
        ) : null}

        {/* ── What this answer is, and where it came from ──
            Rendered verbatim: which pieces were asked, why those are the ones,
            why there is no single budget, and which of the three positions
            answered. A screen that aggregates without saying what it aggregated
            is a number nobody can check. */}
        {atAll && tabMeta && (
          <div className="bm-kw-panel" style={{ marginBottom: 18 }}>
            <div className="bm-kw-row">
              <span className="bm-kw-lab">The website</span>
              <span className="bm-kw-note">{rows.length} {tab === "posts" ? "post" : "page"} row(s) on screen, from {sectionReport.length} piece(s){handoverCount ? `, ${handoverCount} of them read only` : ""}. {tabMeta.sections_why}</span>
            </div>
            {sectionReport.map(s => (
              <div className="bm-kw-row" key={s.section_id}>
                <span className="bm-kw-chip">{s.section_name}</span>
                <span className={"bm-kw-note" + (s.error ? " bm-kw-note--bad" : "")}>
                  {s.live} row(s){s.capped ? ", which is all one read takes, so there are more" : ""} · {s.verb === "save" ? "Save" : "Copy the handover brief"} · title budget {s.budget.limit}
                  {s.title_suffix ? ` (${TITLE_LIMIT} less the "${s.title_suffix}" this front end appends)` : " (no suffix found on this front end)"}
                  {s.error ? ` · could not be read: ${s.error}` : ""}
                  {s.why ? ` ${s.why}` : ""}
                </span>
              </div>
            ))}
            <p className="bm-kw-note">{tabMeta.budget_why}</p>
            {tabMeta.scope ? (
              <p className="bm-kw-note">
                Answered at the <b>{tabMeta.scope.kind}</b>
                {tabMeta.scope.read_from ? `, reading stored meta by ${tabMeta.scope.read_from}` : ""}.
                {tabMeta.scope.read_from_why ? ` ${tabMeta.scope.read_from_why}` : ""}
              </p>
            ) : null}
            {tabMeta.error ? <p className="bm-kw-note bm-kw-note--bad">This tab could not be read at all: {tabMeta.error}</p> : null}
          </div>
        )}

        {/* One piece being down is not the website being down, so the rows that
            did arrive are on screen and the piece that failed is named here. */}
        {atAll && brokenSections.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            {React.createElement(DS.Banner, { tone: "warning", title: brokenSections.length === 1 ? "One piece of this website could not be read" : brokenSections.length + " pieces of this website could not be read" },
              <div>
                <ul className="bm-blocked">
                  {brokenSections.map(s => <li key={s.section_id}><b>{s.section_name}</b>: {s.error}. {s.why}</li>)}
                </ul>
                <p style={{ margin: "8px 0 0" }}>Every other piece's rows are listed as usual. This is a connection problem rather than an empty section, so the rows it holds are neither missing nor changed.</p>
              </div>)}
          </div>
        )}

        {blocked.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            {React.createElement(DS.Banner, { tone: "danger", title: blocked.length === 1 ? "One post targets more keywords than it can save" : blocked.length + " posts target more keywords than they can save" },
              <div>
                <p style={{ margin: "0 0 8px" }}>Nothing is being trimmed for you. Two keywords that deserve separate articles, forced onto one page, compete and neither ranks, so the terms below are left exactly where you typed them and the save waits.</p>
                <ul className="bm-blocked">
                  {blocked.map(({ row, kw }) => (
                    <li key={row.id}><b>{row.title}</b>: {kw.advice.count} secondary keywords beside <b>{kw.primary}</b>. Over the line: {kw.spare.join(", ")}.</li>
                  ))}
                </ul>
              </div>)}
          </div>
        )}

        {/* ── Save, per section, with no button that writes the website ──
            One per piece that has edited rows, each through that piece's own
            address. The sentence is there because the missing button is the
            thing somebody will look for. */}
        {atAll && dirtySections.length > 0 && (
          <div className="bi-bulk-bar" style={{ marginBottom: 18 }}>
            <span className="bi-bulk-bar__count">{dirtyCount} edited row(s)</span>
            {dirtySections.map(g => (
              <button key={String(g.key)} className="bi-btn bi-btn--sm bi-btn--primary" disabled={saving || blocked.length > 0 || !g.address}
                      title={g.address ? `Writes through ${g.address}` : "This screen has no address for that section, so nothing can be sent"}
                      onClick={() => save(g.key)}>
                {saving && savingWhat === g.name
                  ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Saving…</>
                  : <><I name="check" size={14} />Save {g.rows} in {g.name}</>}
              </button>
            ))}
            <span className="bi-bulk-bar__sep" />
            <span className="bm-kw-note">Saving is per piece of the website, because a post id means nothing outside the CMS it came from. There is deliberately no one button that writes all of them.</span>
          </div>
        )}

        {selectMode && (
          <div className="bi-bulk-bar">
            <span className="bi-bulk-bar__count">{sel.length} selected{atAll && pickedHeldCount ? `, ${pickedWritableCount} writable` : ""}</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel(rows.map(r => r.id))}><I name="check-square" size={14} />Select all ({rows.length})</button>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
            <span className="bi-bulk-bar__sep" />
            <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={!sel.length || !!bulkBusy} onClick={aiFillSelected}>
              <I name="sparkles" size={14} />{!bulkBusy ? "AI Fill SEO"
                : bulkBusy.phase === "excerpts" ? `Reading ${bulkBusy.total} excerpt(s)${bulkBusy.where || ""}…`
                : `Writing ${bulkBusy.total} row(s)${bulkBusy.where || ""}…`}
            </button>
            {/* The action stays on the line and the pattern opens below it,
                which is the house shape for anything needing more than a
                click: the bar does not grow a text field everybody has to
                scroll past when they only came to clear a description. */}
            <button className={"bi-btn bi-btn--sm" + (tplOpen ? " on" : "")} disabled={!sel.length || !!bulkBusy} onClick={() => setTplOpen(o => !o)} aria-expanded={tplOpen}><I name="type" size={14} />Apply template</button>
            <button className="bi-btn bi-btn--sm" disabled={!sel.length || !!bulkBusy} onClick={clearMetaSelected}><I name="x-circle" size={14} />Clear meta</button>
            {/* The other verb, in the bar, for the rows the three above hold
                back. It appears only where such a row exists, because a button
                for a thing nobody selected is noise. */}
            {handoverCount > 0 && (
              <button className="bi-btn bi-btn--sm" disabled={!pickedHeldCount || !!bulkBusy} onClick={copyBriefsSelected}><I name="copy" size={14} />{`Copy ${pickedHeldCount ? pickedHeldCount + " " : ""}handover brief${pickedHeldCount === 1 ? "" : "s"}`}</button>
            )}
          </div>
        )}

        {selectMode && tplOpen && (
          <div className="bm-tpl">
            <div className="bm-tpl__row">
              <label className="bm-tpl__lab" htmlFor="bm-tpl-pattern">Title pattern</label>
              <input id="bm-tpl-pattern" className="bm-field bm-tpl__input" value={tpl} onChange={e => setTpl(e.target.value)} placeholder="{title} | {site}" />
              <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={!sel.length || !!bulkBusy} onClick={applyTemplateSelected}>
                <I name="check" size={14} />Apply to {pickedWritableCount} row{pickedWritableCount === 1 ? "" : "s"}
              </button>
            </div>
            {/* The preview is before the apply, not after it. A pattern is one
                typo away from writing a trailing pipe or a literal token onto
                every selected row, and the only cheap place to catch that is
                on screen with a real row's text in it. The counter beside it is
                that ROW's budget: at All the same pattern can fit one section
                and not another. */}
            <div className="bm-tpl__preview">
              {tplFirst ? (
                <>
                  <span className="bm-tpl__plab">{sel.length === 1 ? "Result" : "First selected row"}{atAll && tplFirst.sectionName ? ` in ${tplFirst.sectionName}` : ""}</span>
                  <b className="bm-tpl__out">{tplPreview || "(empty)"}</b>
                  {counter(tplPreview.length, tplBudget.limit, tplBudget)}
                  {String(tplFirst.t || "").trim() ? <span className="bm-tpl__skip">This row already has an SEO title, so it will be left alone.</span> : null}
                  {!tplFirst.canSave ? <span className="bm-tpl__skip">This row has no CMS behind it, so a template cannot be applied to it at all.</span> : null}
                </>
              ) : <span className="bm-tpl__plab">Tick a row to see what the pattern produces.</span>}
            </div>
            <p className="bm-kw-note">
              {"{title}"} is the post's own headline and {"{site}"} is {tplSiteName ? `"${tplSiteName}"` : "the site name, which this screen has not been handed yet"}{atAll ? ", the name of the whole website, because one pattern applied across several sections cannot carry a different brand per row" : ""}. Applying fills the SEO title on the selected rows that are still empty and can be written, in the table only: Save stays a separate decision, nothing already written is overwritten, and keywords are never touched.
              {tplUnknown.length ? ` ${tplUnknown.join(", ")} ${tplUnknown.length === 1 ? "is not a token this screen knows, so it" : "are not tokens this screen knows, so they"} would be written out literally.` : ""}
            </p>
          </div>
        )}

        {/* A lookup rather than a chain of ternaries, because there are several
            outcomes now and a whole batch failing is not a "warning": the fill
            has one call per section, so a failure is total for that section and
            reads as danger. */}
        {bulkNote && React.createElement(DS.Banner,
          { tone : BULK_BANNER[bulkNote.tone] ? BULK_BANNER[bulkNote.tone][0] : BULK_BANNER.info[0],
            title: BULK_BANNER[bulkNote.tone] ? BULK_BANNER[bulkNote.tone][1] : BULK_BANNER.info[1],
            onClose: () => setBulkNote(null) },
          bulkNote.text)}

        <div className="kw-table-wrap">
          <table className="kw-table bm-table">
            <thead><tr>
              {selectMode && <th style={{ width: 36 }}><label className="hds-check"><input type="checkbox" checked={allChecked} onChange={() => setSel(allChecked ? [] : rows.map(r => r.id))} /><span className="hds-check__box"><I name="check" size={12} /></span></label></th>}
              <th className="bm-post">{tab === "posts" ? "Post" : "Page"}</th>
              {/* The Section column exists at All and nowhere else. Filtered to
                  one section it is removed and nothing else about the table
                  changes, which is what makes the filter a filter. */}
              {atAll && <th style={{ width: 130 }}>Section</th>}
              <th>SEO title</th><th>Meta description</th><th>Keywords (1 + {MAX_SECONDARY})</th><th style={{ width: atAll ? 150 : 40 }}></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                /* The row opens the drawer and does nothing else. In particular
                   it never touches `sel`: ticking a row for a bulk action and
                   opening it to read are different intents, and a click that
                   did both would silently change what the next bulk action
                   applies to. */
                <tr key={r.id} className={"bm-row" + (isDirty(r) ? " bm-row-dirty" : "") + (sel.includes(r.id) ? " is-captured-row" : "") + (drawerId === r.id ? " bm-row--open" : "")}
                    role="button" tabIndex={0} aria-label={"Open the SEO editor for " + r.title}
                    onClick={e => { if (!fromRowControl(e)) setDrawerId(r.id); }}
                    onKeyDown={e => {
                      // Only the row's own keys. Enter inside the SEO title
                      // input belongs to that input, and a space typed into the
                      // meta description is a space.
                      if (e.target !== e.currentTarget) return;
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault(); // Space on a focused row would scroll the page.
                      setDrawerId(r.id);
                    }}>
                  {selectMode && <td onClick={e => e.stopPropagation()}><label className="hds-check"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label></td>}
                  <td className="bm-post">
                    <div className="bm-post__title">{r.title}</div>
                    <div className="bm-post__sub">{statusBadge(r.status)}{r.link ? <a className="bm-post__view" href={r.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>view <I name="external-link" size={11} /></a> : null}</div>
                  </td>
                  {atAll && (
                    <td style={{ minWidth: 110 }}>
                      <span className="bm-kw-chip">{r.sectionName || "unknown"}</span>
                      <span className="bm-kw" title={r.readFrom === "crawl" ? "Read by crawling the public page: there is no CMS behind this section, so these are the values a visitor really gets." : "Read through this section's own CMS."}>
                        {r.readFrom === "crawl" ? "crawled, read only" : "CMS"}
                      </span>
                    </td>
                  )}
                  {/* THE VERB IS THE ROW'S. A row that can be written keeps the
                      inputs it has always had. A row that cannot shows the same
                      two values as text, counted against ITS OWN section's
                      budget, because an input somebody can type into is a
                      promise that something will be saved. */}
                  <td style={{ minWidth: 220 }}>
                    {r.canSave
                      ? <input className="bm-field" value={r.t} onChange={e => upd(r.id, "t", e.target.value)} placeholder="Add an SEO title" />
                      : <div className="bm-kw-note">{r.t || "(no title served)"}</div>}
                    {counter(r.t.length, budgetOf(r).limit, budgetOf(r))}
                  </td>
                  <td style={{ minWidth: 260 }}>
                    {r.canSave
                      ? <textarea className="bm-field" rows={2} value={r.d} onChange={e => upd(r.id, "d", e.target.value)} placeholder="Add a meta description" />
                      : <div className="bm-kw-note">{r.d || "(no description served)"}</div>}
                    {counter(r.d.length, DESC_LIMIT)}
                  </td>
                  <td style={{ minWidth: 190 }}>
                    {r.canSave
                      ? <>
                          <input className="bm-field" value={r.kw} onChange={e => upd(r.id, "kw", e.target.value)} placeholder="primary, secondary, secondary" />
                          {kwLine(readKeywords(r.kw))}
                        </>
                      : <span className="bm-kw bm-kw--none" title="A focus keyword is a CMS field, and this page has no CMS behind it.">Nowhere to store a keyword</span>}
                  </td>
                  <td>
                    {!r.canSave && (
                      <button className="bi-btn bi-btn--sm" title="Put a brief for whoever builds this part of the site on your clipboard" onClick={() => copyBrief(r)}>
                        <I name="copy" size={14} />Copy the handover brief
                      </button>
                    )}
                    <button className="bi-iconbtn" title="Open editor" onClick={() => setDrawerId(r.id)}><I name="chevron-right" size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading ? <div className="bi-subempty">Loading…</div> : rows.length === 0 && (
            <div className="bi-subempty">
              No {tab} found{atAll ? " on any piece of this website" : ""}.
              {atAll && sectionReport.length > 0 ? " The report above says what each piece contributed and why." : ""}
            </div>
          )}
        </div>

        {drawerRow && <MetaDrawer row={drawerRow} siteId={siteId} budget={budgetOf(drawerRow)} blockedNote={blockedNote} onClose={() => setDrawerId(null)} onChange={(f, v) => upd(drawerRow.id, f, v)} onCopyBrief={() => copyBrief(drawerRow)} onSave={async () => { if (blocked.length) return; await save(atAll ? drawerRow.sectionId : undefined); setDrawerId(null); }} saving={saving} />}
      </div>
    );
  }

  window.MlSeoBulkMeta = { BulkMeta };
})();
